import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Check, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { InfoHint } from "@dolfin/ui";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import { CopyButton } from "@/components/ui/copy-button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDateTime } from "@/lib/format";
import { PaginationBar } from "@/components/PaginationBar";
import { useCursorPagination } from "@/hooks/useCursorPagination";

type ReceivedEInvoice = components["schemas"]["ReceivedEInvoiceAdminResponse"];
type ReceivedEInvoiceState = NonNullable<components["schemas"]["ReceivedEInvoiceState"]>;
type EInvoiceFault = components["schemas"]["EInvoiceFault"];
type ProviderFaultDetail = components["schemas"]["ProviderFaultDetail"];

interface ReceivedEInvoicesTableProps {
  routedSuccess?: boolean;
  state?: ReceivedEInvoiceState;
  /** SDI's IdentificativoSdI (or another network's id) to find one document by. */
  networkReference?: string;
  /** Bumping this triggers a refetch. */
  refreshKey: number;
}

/** Nothing to show. A single hyphen, per the house rule on user-visible text. */
const EMPTY = "-";

function stateVariant(state: string): "default" | "secondary" | "outline" | "destructive" {
  switch (state) {
    case "Routed":
      return "default";
    case "Unroutable":
      return "secondary";
    case "Unreadable":
      return "destructive";
    default:
      return "outline";
  }
}

/** Decimals cross the wire as strings on some routes and numbers on others, so both are handled. */
function formatMoney(
  amount: string | number | null | undefined,
  currency: string | null | undefined,
) {
  if (amount === null || amount === undefined) return EMPTY;
  const value = typeof amount === "string" ? Number(amount) : amount;
  if (!Number.isFinite(value)) return EMPTY;
  return `${value.toFixed(2)} ${currency ?? ""}`.trim();
}

/** Our faults and the provider's lead to different work; the row indicator sums them, the sheet separates them. */
function faultCounts(r: ReceivedEInvoice) {
  const ours = r.faults?.length ?? 0;
  const provider = r.providerFaults?.length ?? 0;
  return { ours, provider, total: ours + provider };
}

function faultSummary(ours: number, provider: number) {
  const parts: string[] = [];
  if (ours) parts.push(`${ours} processing ${ours === 1 ? "fault" : "faults"}`);
  if (provider) parts.push(`${provider} provider ${provider === 1 ? "fault" : "faults"}`);
  return parts.join(", ");
}

/** The at-a-glance answer: a tick means the document arrived and processed clean. */
function FaultIndicator({ received }: { received: ReceivedEInvoice }) {
  const { ours, provider, total } = faultCounts(received);

  if (total === 0) {
    return (
      <span className="inline-flex items-center text-muted-foreground/40">
        <Check className="size-4" aria-hidden />
        <span className="sr-only">No faults</span>
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5 text-amber-600 dark:text-amber-400">
      <AlertTriangle className="size-4 shrink-0" aria-hidden />
      <span className="text-xs font-medium tabular-nums">{total}</span>
      <span className="sr-only">{faultSummary(ours, provider)}</span>
    </span>
  );
}

export function ReceivedEInvoicesTable({
  routedSuccess,
  state,
  networkReference,
  refreshKey,
}: ReceivedEInvoicesTableProps) {
  const [received, setReceived] = useState<ReceivedEInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<ReceivedEInvoice | null>(null);

  // Keyset, not offset: documents keep arriving at the front, so an offset page would repeat rows.
  const pagination = useCursorPagination({ initialLimit: 50 });

  // Reset the cursor during render and in state, not an effect or a ref: either would let the stale cursor
  // go out against the new filter.
  const filterKey = `${routedSuccess ?? ""}|${state ?? ""}|${networkReference ?? ""}`;
  const [cursorFilterKey, setCursorFilterKey] = useState(filterKey);
  if (cursorFilterKey !== filterKey) {
    setCursorFilterKey(filterKey);
    pagination.reset();
  }

  useEffect(() => {
    let cancelled = false;

    async function fetchReceived() {
      setLoading(true);
      setError(null);
      const { data, error: apiError } = await api.GET("/admin/received-e-invoices", {
        params: {
          query: { routedSuccess, state, networkReference, cursor: pagination.cursor, limit: pagination.limit },
        },
      });
      if (cancelled) return;
      if (apiError) {
        setError("Failed to load received e-invoices.");
      } else {
        setReceived(data?.data ?? []);
        pagination.setPage({
          nextCursor: data?.meta?.cursors?.next,
          previousCursor: data?.meta?.cursors?.previous,
        });
      }
      setLoading(false);
    }

    fetchReceived();
    return () => {
      cancelled = true;
    };
  }, [routedSuccess, state, networkReference, refreshKey, pagination.cursor, pagination.limit]);

  if (error) {
    return <p className="text-destructive">{error}</p>;
  }

  // Only the first load blanks the table: the 60s refetch must not unmount the open sheet or lose scroll.
  if (loading && received.length === 0) {
    return <Spinner />;
  }

  if (received.length === 0) {
    return (
      <div>
        <p className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
          {networkReference
            ? `No received document has network id ${networkReference}. If it was sent rather than received, try Sent E-Invoices. `
            : ""}
          Nothing here. With the default filter that means every document delivered to us has found an
          organisation - which is what it should say most of the time.
        </p>
        {/* Still shown: paging forward onto an empty page has to be escapable. */}
        <PaginationBar
          hasPrevious={pagination.hasPrevious}
          hasNext={pagination.hasMore}
          isLoading={loading}
          limit={pagination.limit}
          limitOptions={[20, 50, 100]}
          onPrevious={pagination.goPrevious}
          onNext={pagination.goNext}
          onLimitChange={pagination.setLimit}
        />
      </div>
    );
  }

  return (
    <div>
      {/* The strip keeps its height so the table doesn't jump on each refresh. */}
      <div className="flex h-5 items-center justify-end">
        {loading && <Spinner className="size-4" />}
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>State</TableHead>
            <TableHead>
              <span className="inline-flex items-center gap-1">
                Faults
                <InfoHint label="Faults">
                  Both accounts of what went wrong, added together: our processing faults and the
                  ones the provider reported. Open the row to read them apart.
                </InfoHint>
              </span>
            </TableHead>
            <TableHead>Addressed to</TableHead>
            <TableHead>Supplier</TableHead>
            <TableHead>Invoice</TableHead>
            <TableHead className="text-right">Amount</TableHead>
            <TableHead>Received</TableHead>
            <TableHead className="text-right">Waiting</TableHead>
            <TableHead>Organisation</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {received.map((r) => (
            <TableRow
              key={r.id ?? r.siloEntryId}
              className="cursor-pointer"
              onClick={() => setSelected(r)}
            >
              <TableCell>
                <Badge variant={stateVariant(r.state ?? "")}>{r.state}</Badge>
              </TableCell>
              <TableCell>
                <FaultIndicator received={r} />
              </TableCell>
              <TableCell className="font-mono text-xs">
                {r.customerElectronicAddress ?? r.customerTaxId ?? (
                  <span className="text-muted-foreground">unaddressed</span>
                )}
              </TableCell>
              <TableCell className="max-w-[16rem] truncate">{r.supplierName ?? EMPTY}</TableCell>
              <TableCell className="font-mono text-xs">{r.invoiceNumber ?? EMPTY}</TableCell>
              <TableCell className="text-right tabular-nums">
                {formatMoney(r.totalAmount, r.currency)}
              </TableCell>
              <TableCell className="text-xs text-muted-foreground">
                {formatDateTime(r.receivedAt)}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {/* Minutes is the sweep doing its job; days means someone sends to a participant we don't recognise. */}
                {r.routedSuccess ? EMPTY : `${r.daysUnrouted ?? 0}d`}
              </TableCell>
              <TableCell>
                {r.organisationId ? (
                  <Link
                    to={`/organisations/${r.organisationId}`}
                    className="text-primary hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    View
                  </Link>
                ) : (
                  <span className="text-muted-foreground">none</span>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <PaginationBar
        hasPrevious={pagination.hasPrevious}
        hasNext={pagination.hasMore}
        isLoading={loading}
        limit={pagination.limit}
        limitOptions={[20, 50, 100]}
        onPrevious={pagination.goPrevious}
        onNext={pagination.goNext}
        onLimitChange={pagination.setLimit}
      />

      <ReceivedEInvoiceSheet
        received={selected}
        onClose={() => setSelected(null)}
        onReprocessed={(updated) => {
          setSelected(updated);
          setReceived((rows) => rows.map((row) => (row.id === updated.id ? updated : row)));
        }}
      />
    </div>
  );
}

/** Everything the route returns, printed even when empty: "no tax number sent" is itself why a document couldn't be placed. */
/** Reprocess anything not yet a bill or credit note; the backend refuses routed documents. */
function canReprocess(received: ReceivedEInvoice) {
  return received.state !== "Routed";
}

function ReceivedEInvoiceSheet({
  received,
  onClose,
  onReprocessed,
}: {
  received: ReceivedEInvoice | null;
  onClose: () => void;
  onReprocessed: (updated: ReceivedEInvoice) => void;
}) {
  const [reprocessing, setReprocessing] = useState(false);

  async function reprocess() {
    if (!received?.id || reprocessing) return;
    setReprocessing(true);
    try {
      const { data, error } = await api.POST("/admin/received-e-invoices/{id}/reprocess", {
        params: { path: { id: received.id } },
      });
      if (error || !data) {
        toast.error((error as { detail?: string } | undefined)?.detail ?? "Could not reprocess this document.");
        return;
      }
      onReprocessed(data);
      toast.success(data.state === "Routed" ? "Reprocessed: placed with its organisation." : `Reprocessed: now ${data.state}.`);
    } finally {
      setReprocessing(false);
    }
  }

  return (
    <Sheet open={!!received} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        className="w-full overflow-y-auto sm:max-w-xl"
        // Radix focuses the first focusable child (a copy button), popping its tooltip; focus the panel instead.
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          (e.currentTarget as HTMLElement | null)?.focus();
        }}
      >
        <SheetHeader>
          <SheetTitle>{received?.supplierName ?? "Received e-invoice"}</SheetTitle>
          <SheetDescription>
            {received?.invoiceNumber ?? "No invoice number"} ·{" "}
            {formatMoney(received?.totalAmount, received?.currency)}
          </SheetDescription>
        </SheetHeader>

        {received && (
          <div className="space-y-6 p-4 text-sm">
            {canReprocess(received) ? (
              <span className="inline-flex items-center gap-1.5">
                <Button variant="outline" size="sm" disabled={reprocessing} onClick={() => void reprocess()}>
                  {reprocessing ? <Spinner className="size-4" /> : <RefreshCw className="size-4" />}
                  Reprocess
                </Button>
                <InfoHint label="Reprocess">
                  Reads this document back from Invopop and runs ingestion again: finds the organisation it is
                  addressed to, then creates the bill or supplier credit note. Use it after fixing the cause, for
                  example registering this participant id on an organisation, or a correction on the provider's
                  side. It is the only way out of Unreadable; for Received or Unroutable it does now what the
                  background sweeps would do later. Safe to repeat: it never creates a second bill.
                </InfoHint>
              </span>
            ) : null}
            <Section title="Delivery">
              <Field label="State">
                <Badge variant={stateVariant(received.state ?? "")}>
                  {received.state ?? "unknown"}
                </Badge>
              </Field>
              <Field label="Placed with an organisation">
                {received.routedSuccess ? "Yes" : "No"}
              </Field>
              <Field label="Delivered">{formatDateTime(received.receivedAt)}</Field>
              <Field label="Placed">
                {received.routedAt ? formatDateTime(received.routedAt) : "not yet"}
              </Field>
              <Field label="Waiting">
                {/* Days only mean something while the document is still unplaced. */}
                {received.routedSuccess ? EMPTY : `${received.daysUnrouted ?? 0} days`}
              </Field>
            </Section>

            <Section title="Document">
              <Field label="Supplier">{received.supplierName ?? EMPTY}</Field>
              <Field label="Invoice number" mono>
                {received.invoiceNumber ?? EMPTY}
              </Field>
              <Field label="Network id (SDI)" mono>
                <Copyable value={received.networkReference}>
                  {received.networkReference ?? "none recorded"}
                </Copyable>
              </Field>
              <Field label="Total amount">
                {formatMoney(received.totalAmount, received.currency)}
              </Field>
              <Field label="Currency">{received.currency ?? EMPTY}</Field>
              <Field label="Addressed to" mono>
                <Copyable value={received.customerElectronicAddress}>
                  {received.customerElectronicAddress ?? "no participant id"}
                </Copyable>
              </Field>
              <Field label="Customer tax id" mono>
                <Copyable value={received.customerTaxId}>
                  {received.customerTaxId ?? "none given"}
                </Copyable>
              </Field>
            </Section>

            {/* Our faults and the provider's are kept apart: they lead to different work. */}
            <Section title="Faults">
              <Field label="Our processing faults" full>
                <FaultList faults={received.faults} />
              </Field>
              <Field label="Provider faults" full>
                <FaultList faults={received.providerFaults} mono />
              </Field>
            </Section>

            <Section title="Provider">
              <Field label="Event">{received.providerEvent ?? EMPTY}</Field>
              <Field label="Job" mono>
                <Copyable value={received.providerJobId}>
                  {received.providerJobId ?? EMPTY}
                </Copyable>
              </Field>
            </Section>

            <Section title="Identifiers">
              <Field label="Delivery" mono>
                <Copyable value={received.id}>{received.id ?? EMPTY}</Copyable>
              </Field>
              <Field label="Silo entry" mono>
                <Copyable value={received.siloEntryId}>{received.siloEntryId}</Copyable>
              </Field>
              <Field label="Organisation" mono>
                {received.organisationId ? (
                  <span className="flex items-start gap-2">
                    <Link
                      to={`/organisations/${received.organisationId}`}
                      className="break-all text-primary hover:underline"
                    >
                      {received.organisationId}
                    </Link>
                    <CopyButton value={received.organisationId} className="mt-0.5 shrink-0" />
                  </span>
                ) : (
                  <span className="font-sans text-muted-foreground">none</span>
                )}
              </Field>
              <Field label="Organisation document" mono>
                {/* No page for it in this dash, so the id is offered to copy rather than linked to a 404. */}
                <Copyable value={received.organisationEInvoiceId}>
                  {received.organisationEInvoiceId ?? "not ingested"}
                </Copyable>
              </Field>
            </Section>

            {!received.routedSuccess && (
              <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-muted-foreground">
                Nothing needs re-sending. Register the organisation for this participant id, or
                correct its tax number, and the routing sweep places this document within fifteen
                minutes.
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="mb-3 border-b pb-1 text-xs font-semibold uppercase tracking-wide text-foreground">
        {title}
      </h3>
      <dl className="grid [&>div]:min-w-0 grid-cols-2 gap-x-4 gap-y-4">{children}</dl>
    </div>
  );
}

function Field({
  label,
  children,
  mono,
  full,
}: {
  label: string;
  children: React.ReactNode;
  /** Ids and codes read better fixed-width and small. */
  mono?: boolean;
  /** Span both columns, for lists. */
  full?: boolean;
}) {
  return (
    <div className={full ? "col-span-2" : undefined}>
      <dt className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className={mono ? "font-mono text-xs break-all" : undefined}>{children}</dd>
    </div>
  );
}

/** An id worth copying, or the same text plainly when there is nothing to copy. */
function Copyable({ value, children }: { value?: string | null; children: React.ReactNode }) {
  if (!value) {
    return <span className="font-sans text-muted-foreground">{children}</span>;
  }
  // Ids wrap rather than truncate: a half-printed uuid tells the reader nothing.
  return (
    <span className="flex items-start gap-2">
      <span className="break-all">{children}</span>
      <CopyButton value={value} className="mt-0.5 shrink-0" />
    </span>
  );
}

// Takes both shapes: our faults carry a severity, the provider's carry its own code.
function FaultList({
  faults,
  mono,
}: {
  faults?: (EInvoiceFault | ProviderFaultDetail)[];
  mono?: boolean;
}) {
  if (!faults || faults.length === 0) {
    return <span className="text-muted-foreground">none recorded</span>;
  }
  return (
    <ul className="space-y-1">
      {faults.map((fault, i) => {
        const message = fault.message ?? fault.code;
        const stop = "severity" in fault && fault.severity === "Stop";
        return (
          <li key={i} className="flex items-start gap-1.5 text-muted-foreground">
            <AlertTriangle
              className={
                stop
                  ? "mt-0.5 size-3 shrink-0 text-red-600 dark:text-red-400"
                  : "mt-0.5 size-3 shrink-0 text-amber-600 dark:text-amber-400"
              }
            />
            <span className={mono ? "font-mono text-xs break-all" : undefined}>{message}</span>
          </li>
        );
      })}
    </ul>
  );
}
